import axios from 'axios';
import {
    Driver,
    DriverPackageWithDriver,
    PaginatedResponse,
} from '../types/driver_types.ts';
import { DI_API_ENDPOINT } from '../config/constants.ts';

export class ApiTokenRetrievalError extends Error {
    constructor() {
        super('Error during retrieval of API token');
        this.name = 'ApiTokenRetrievalError';
        Object.setPrototypeOf(this, ApiTokenRetrievalError.prototype);
    }
}

const getCommonHeaders = (token?: string) => {
    const headers: Record<string, string> = {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        Expires: '0',
    };
    if (token) {
        headers['Authorization'] = `Token ${token}`;
    }
    return headers;
};

export const fetchDriverList = async (
    token?: string,
    pageParam?: string,
): Promise<PaginatedResponse<Driver>> => {
    let url = pageParam || `${DI_API_ENDPOINT}/drivers/`;

    // The API might return absolute URLs that don't match our DI_API_ENDPOINT
    // (e.g. http://testserver vs http://localhost:8000)
    if (url.startsWith('http://testserver')) {
        url = url.replace('http://testserver', DI_API_ENDPOINT);
    } else if (!url.startsWith('http')) {
        url = `${DI_API_ENDPOINT}${url}`;
    }

    const result = await axios.get(url, {
        headers: getCommonHeaders(token),
    });

    if (result.status !== 200) {
        throw new Error(`Failed to fetch driver list: ${result.statusText}`);
    }

    return result.data as PaginatedResponse<Driver>;
};

export const fetchDriverDetails = async (driverId: number, token?: string) => {
    const result = await axios.get(`${DI_API_ENDPOINT}/drivers/${driverId}/`, {
        headers: getCommonHeaders(token),
    });

    if (result.status !== 200) {
        throw new Error(`Failed to fetch driver details: ${result.statusText}`);
    }

    return result.data;
};

export const fetchDriverPackage = async (
    packageId: number,
    token?: string,
): Promise<DriverPackageWithDriver> => {
    const result = await axios.get(
        `${DI_API_ENDPOINT}/driver_packages/${packageId}/`,
        {
            headers: getCommonHeaders(token),
        },
    );

    if (result.status !== 200) {
        throw new Error(`Failed to fetch driver package: ${result.statusText}`);
    }

    return result.data;
};

type KnoxTokenResponse = {
    token: string;
    expiry: number | undefined;
};

export const fetchAPIKnoxToken = async (
    idToken: string,
): Promise<KnoxTokenResponse | undefined> => {
    try {
        const response = await axios.post(
            `${DI_API_ENDPOINT}/auth/login/`,
            {}, // data
            {
                headers: {
                    'Azure-Jwt-Authorization': idToken,
                },
            },
        );
        if (response.status !== 200) {
            return undefined;
        }

        return response.data as KnoxTokenResponse;
    } catch {
        return undefined;
    }
};

export const logoutFromAPI = async (token?: string) => {
    try {
        const response = await axios.post(
            `${DI_API_ENDPOINT}/auth/logout/`,
            {}, // data
            {
                headers: getCommonHeaders(token),
            },
        );
        return response.status === 200;
    } catch {
        return false;
    }
};

export const isApiTokenExpired = (tokenExpiration: number | undefined) => {
    if (tokenExpiration) {
        return tokenExpiration < Date.now() / 1000.0;
    }
    // undefined expiration is considered valid
    return false;
};
